import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { MapPin, Building2, Users, Briefcase } from 'lucide-react';

const DRC_PROVINCES = [
  'Kinshasa', 'Kongo Central', 'Kwango', 'Kwilu', 'Mai-Ndombe',
  'Kasai', 'Kasai-Central', 'Kasai-Oriental', 'Lomami', 'Sankuru',
  'Maniema', 'Sud-Kivu', 'Nord-Kivu', 'Ituri', 'Haut-Uele',
  'Tshopo', 'Bas-Uele', 'Nord-Ubangi', 'Mongala', 'Sud-Ubangi',
  'Equateur', 'Tshuapa', 'Tanganyika', 'Haut-Lomami', 'Lualaba', 'Haut-Katanga',
] as const;

const PILLARS = ['critical_minerals', 'industrialization', 'infrastructure', 'governance'] as const;
const SECTORS = ['mining', 'processing', 'infrastructure', 'industrial', 'services', 'other'] as const;

const PILLAR_DOT_COLORS: Record<string, string> = {
  critical_minerals: 'bg-spa-minerals',
  industrialization: 'bg-spa-industrialization',
  infrastructure: 'bg-spa-infrastructure',
  governance: 'bg-spa-governance',
};

const PILLAR_BADGE_STYLES: Record<string, string> = {
  critical_minerals: 'bg-spa-minerals/10 text-spa-minerals border-spa-minerals/30',
  industrialization: 'bg-spa-industrialization/10 text-spa-industrialization border-spa-industrialization/30',
  infrastructure: 'bg-spa-infrastructure/10 text-spa-infrastructure border-spa-infrastructure/30',
  governance: 'bg-spa-governance/10 text-spa-governance border-spa-governance/30',
};

interface SpaOpp {
  id: string;
  province: string | null;
  spa_pillar: string | null;
  sector: string;
}

function getProvinceStyle(count: number) {
  if (count === 0) return 'bg-muted/50 border-border';
  if (count <= 2) return 'bg-secondary/5 border-secondary/20';
  if (count <= 5) return 'bg-secondary/10 border-secondary/40';
  return 'bg-secondary/20 border-secondary/60';
}

export default function SpaMap() {
  const { t } = useTranslation();
  const [spaOpps, setSpaOpps] = useState<SpaOpp[]>([]);
  const [spaCompanyCount, setSpaCompanyCount] = useState(0);
  const [totalProfessionals, setTotalProfessionals] = useState(0);
  const [diasporaCount, setDiasporaCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pillarFilter, setPillarFilter] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [oppsRes, companiesRes, profilesRes, diasporaRes] = await Promise.all([
        supabase.from('opportunities').select('id, province, spa_pillar, sector').eq('spa_linked', true).eq('status', 'open'),
        supabase.from('companies').select('id', { count: 'exact', head: true }).eq('spa_linked', true).eq('verified', true),
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('is_diaspora', true),
      ]);

      setSpaOpps((oppsRes.data as SpaOpp[]) || []);
      setSpaCompanyCount(companiesRes.count || 0);
      setTotalProfessionals(profilesRes.count || 0);
      setDiasporaCount(diasporaRes.count || 0);
    } catch (error) {
      console.error('Error fetching map data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Derived data
  const filteredOpps = pillarFilter ? spaOpps.filter(o => o.spa_pillar === pillarFilter) : spaOpps;

  const pillarCounts = PILLARS.reduce((acc, p) => {
    acc[p] = spaOpps.filter(o => o.spa_pillar === p).length;
    return acc;
  }, {} as Record<string, number>);

  const provinceData = DRC_PROVINCES.map(prov => {
    const provOpps = filteredOpps.filter(o => o.province === prov);
    const pillarsPresent = PILLARS.filter(p => provOpps.some(o => o.spa_pillar === p));
    return { name: prov, count: provOpps.length, pillars: pillarsPresent };
  });

  const sectorData = SECTORS.map(s => ({
    name: t(`sectors.${s}`),
    count: spaOpps.filter(o => o.sector === s).length,
  })).filter(d => d.count > 0).sort((a, b) => b.count - a.count);

  if (loading) {
    return (
      <Layout>
        <div className="container py-8 space-y-8">
          <Skeleton className="h-10 w-64" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
          </div>
          <Skeleton className="h-8 w-48" />
          <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-8 space-y-10">
        <h1 className="text-3xl font-bold">{t('map.title')}</h1>
        <p className="mt-2 text-muted-foreground">{t('map.description')}</p>

        {/* Stats bar */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <Briefcase className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{spaOpps.length}</p>
                <p className="text-xs text-muted-foreground">SPA-Linked Opportunities</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <p className="mb-2 text-xs font-medium text-muted-foreground">By Pillar</p>
              <div className="flex flex-wrap gap-1.5">
                {PILLARS.map(p => (
                  <span key={p} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${PILLAR_BADGE_STYLES[p]}`}>
                    {t(`spaPillars.${p}`)} <span className="font-bold">{pillarCounts[p]}</span>
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary/10">
                <Building2 className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{spaCompanyCount}</p>
                <p className="text-xs text-muted-foreground">SPA-Linked Companies</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent/20">
                <Users className="h-6 w-6 text-accent-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalProfessionals}</p>
                <p className="text-xs text-muted-foreground">
                  {totalProfessionals} total · {diasporaCount} diaspora
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Province Grid */}
        <section>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-xl font-semibold">{t('map.byProvince')}</h2>
            <p className="text-sm text-muted-foreground">{t('map.clickToFilter')}</p>
          </div>

          {/* Pillar filter tabs */}
          <div className="mb-6 flex flex-wrap gap-1">
            <Button variant={pillarFilter === null ? 'default' : 'outline'} size="sm" onClick={() => setPillarFilter(null)}>
              All
            </Button>
            {PILLARS.map(p => (
              <Button
                key={p}
                variant={pillarFilter === p ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPillarFilter(pillarFilter === p ? null : p)}
              >
                {t(`spaPillars.${p}`)}
              </Button>
            ))}
          </div>

          <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {provinceData.map(prov => (
              <Link
                key={prov.name}
                to={`/opportunities?province=${encodeURIComponent(prov.name)}&spa=true`}
                className={`group block rounded-lg border-2 p-4 transition-all hover:scale-105 hover:shadow-lg ${getProvinceStyle(prov.count)}`}
              >
                <p className="font-bold text-sm">{prov.name}</p>
                <p className="text-2xl font-bold mt-1">{prov.count}</p>
                {prov.pillars.length > 0 && (
                  <div className="mt-2 flex gap-1">
                    {prov.pillars.map(p => (
                      <span key={p} className={`inline-block h-2.5 w-2.5 rounded-full ${PILLAR_DOT_COLORS[p]}`} />
                    ))}
                  </div>
                )}
              </Link>
            ))}
          </div>
        </section>

        {/* Sector Breakdown */}
        {sectorData.length > 0 && (
          <section>
            <h2 className="mb-4 text-xl font-semibold">{t('map.sectorBreakdown')}</h2>
            <Card>
              <CardContent className="p-6">
                <ResponsiveContainer width="100%" height={sectorData.length * 50 + 40}>
                  <BarChart data={sectorData} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 13 }} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} fill="hsl(217, 91%, 35%)">
                      <LabelList dataKey="count" position="right" className="fill-foreground text-sm font-medium" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </section>
        )}
      </div>
    </Layout>
  );
}
